import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { Box, Backdrop, CircularProgress } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import { useTranslate } from "src/locales";
import { useAppSelector } from "src/app/hooks";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { PediatricAssessmentFormData, PediatricAssessmentCreateProps, AlertState } from "../model";
import { mapPediatricToApi, mapPediatricToUpdateApi } from "../utils";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import { referenceService } from "src/api/references/referenceService";
import { useMcpCache } from "src/hooks/useMcpCache";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";

export default function PediatricAssessmentCreate(props: Readonly<PediatricAssessmentCreateProps>) {
    const {
        recordId,
        patientId,
        formData: parentFormData,
        onChange: parentOnChange,
        extraAccess,
        save,
        isSaving,
        onSaved,
        onCloseModal,
    } = props;
    const { t } = useTranslate();
    const authUser = useAppSelector((state) => state.auth.user);

    const displayUser = useMemo(() => {
        return authUser?.username || "";
    }, [authUser]);

    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState<AlertState>({
        open: false,
        message: "",
        type: "success",
    });

    const mcpIds = useMemo(() => [896, 897, 898, 899], []);
    const { cacheLoaded, getMcpParameterDetails } = useMcpCache(mcpIds);

    const showAlert = (msg: string, type: "success" | "error") => {
        setAlert({
            open: true,
            message: msg,
            type,
        });
    };

    const initialSnapshotRef = useRef<Readonly<PediatricAssessmentFormData> | null>(null);

    const [formData, setFormData] = useState<PediatricAssessmentFormData>({
        evaulation: null,
        ped_general_view: null,
        ped_cardiovascular: null,
        ped_respiratory: null,
        ptime: new Date(),
    });

    const [selectedMCPs, setSelectedMCPs] = useState<Record<string, McParameterDetailResponseDto | null>>({
        evaulation: null,
        ped_general_view: null,
        ped_cardiovascular: null,
        ped_respiratory: null,
    });

    const PEDIATRIC_FIELDS: readonly (keyof PediatricAssessmentFormData)[] = useMemo(() => [
        "evaulation", "ped_general_view", "ped_cardiovascular", "ped_respiratory", "ptime"
    ], []);

    const isValuesEqual = useCallback((val1: any, val2: any): boolean => {
        if (val1 instanceof Date && val2 instanceof Date) {
            return val1.getTime() === val2.getTime();
        }
        const normalize = (v: any) => (v === null || v === undefined || v === 0) ? "" : String(v);
        return normalize(val1) === normalize(val2);
    }, []);

    const checkValidations = useCallback(() => {
        if (!initialSnapshotRef.current) return true;

        const isDirty = PEDIATRIC_FIELDS.some((key) => {
            const cur = (formData as any)[key];
            const init = (initialSnapshotRef.current as any)[key];
            return !isValuesEqual(cur, init);
        });

        if (!isDirty) {
            showAlert(t("pediatric-assessment.no-changes-to-save"), "error");
            return false;
        }

        return true;
    }, [formData, PEDIATRIC_FIELDS, t, isValuesEqual]);

    useEffect(() => {
        if (!cacheLoaded || initialSnapshotRef.current) return;

        if (recordId && parentFormData) {
            const { evaulation, ped_general_view, ped_cardiovascular, ped_respiratory, ptime } = parentFormData;
            const mcps = [896, 897, 898, 899].map((id) => getMcpParameterDetails(id));

            const findDetail = (details: McParameterDetailResponseDto[], val: any) =>
                val ? details.find((d) => String(d.id) === String(val)) || null : null;

            const data: PediatricAssessmentFormData = {
                evaulation: evaulation ?? null,
                ped_general_view: ped_general_view ?? null,
                ped_cardiovascular: ped_cardiovascular ?? null,
                ped_respiratory: ped_respiratory ?? null,
                ptime: ptime ? new Date(ptime as any) : new Date(),
            };

            setFormData(data);
            setSelectedMCPs({
                evaulation: findDetail(mcps[0], evaulation),
                ped_general_view: findDetail(mcps[1], ped_general_view),
                ped_cardiovascular: findDetail(mcps[2], ped_cardiovascular),
                ped_respiratory: findDetail(mcps[3], ped_respiratory),
            });
            initialSnapshotRef.current = { ...data };
        } else if (!recordId) {
            initialSnapshotRef.current = { ...formData };
        }
    }, [recordId, parentFormData, cacheLoaded, getMcpParameterDetails, formData]);

    const handleMCPChange = useCallback((field: keyof PediatricAssessmentFormData, val: McParameterDetailResponseDto | null) => {
        setSelectedMCPs(prev => ({ ...prev, [field]: val }));
        setFormData(prev => ({ ...prev, [field]: val ? Number(val.id) : null }));
        if (parentOnChange) {
            parentOnChange(field, val ? Number(val.id) : null);
        }
    }, [parentOnChange]);

    const handleDateChange = useCallback((date: Date | null) => {
        setFormData(prev => ({ ...prev, ptime: date }));
        if (parentOnChange) {
            parentOnChange("ptime", date);
        }
    }, [parentOnChange]);

    const calculatedScore = useMemo(() => {
        const v1 = selectedMCPs.ped_general_view?.value || 0;
        const v2 = selectedMCPs.ped_cardiovascular?.value || 0;
        const v3 = selectedMCPs.ped_respiratory?.value || 0;
        return (v1 + v2 + v3).toString();
    }, [selectedMCPs]);

    const handleClose = useCallback(() => {
        if (onCloseModal) {
            onCloseModal();
        } else if (extraAccess?.onCloseModal) {
            extraAccess.onCloseModal();
        }
    }, [onCloseModal, extraAccess]);

    const handleSave = useCallback(async () => {
        if (!checkValidations()) return;

        try {
            setSaving(true);

            if (recordId) {
                // UPDATE (PUT)
                const payload = mapPediatricToUpdateApi(formData);
                await referenceService.pediatricAssessment.UpdatePediatricAssessment(Number(recordId), payload);
                showAlert(t("toast.success"), "success");
            } else {
                // CREATE (POST)
                const payload = mapPediatricToApi(formData);
                payload.PT_ID = Number(patientId);
                await referenceService.pediatricAssessment.AddPediatricAssessment(payload);
                showAlert(t("toast.success"), "success");
            }

            // Call optional parent save triggers if provided
            if (save) {
                await save();
            }
            if (onSaved) {
                onSaved();
            }

            // Close modal after success
            setTimeout(() => {
                handleClose();
            }, 1000);

        } catch (error) {
            console.error(error);
            showAlert(t("toast.danger"), "error");
        } finally {
            setSaving(false);
        }
    }, [checkValidations, save, onSaved, t, formData, recordId, patientId, handleClose]);

    return (
        <>
            <Backdrop
                open={saving || !cacheLoaded}
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Box sx={{ padding: '20px', paddingTop: '0px', width: '100%' }}>
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <ByzMuiSaveButton onClick={handleSave} loading={saving || isSaving} disabled={saving || isSaving} />
                </Box>

                <Grid container spacing={2}>
                    <Grid xs={12} sm={6} md={6} container direction={"column"} spacing={3}>
                        <Grid
                            md={6}
                            sx={{
                                backgroundColor: "#F4F7FE",
                                borderBottom: "1px solid #E0E0E0",
                            }}
                        >
                            <ByzMuiTypography isTitle={true}>
                                {t("pediatric-assessment.title")}
                            </ByzMuiTypography>
                        </Grid>

                        {/* Date */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.date")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMuiDateTimePicker
                                    sx={{ width: "100%" }}
                                    value={formData.ptime}
                                    onChange={handleDateChange}
                                />
                            </Grid>
                        </Grid>

                        {/* Recorded By */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("createdby")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMuiTextField
                                    sx={{ width: "100%" }}
                                    color="info"
                                    variant="outlined"
                                    label={t("createdby")}
                                    value={displayUser}
                                    disabled
                                />
                            </Grid>
                        </Grid>

                        {/* Evaluation */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.evaluation")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMcParameterDetailSelect
                                    mcpId={896}
                                    value={selectedMCPs.evaulation}
                                    handleChange={(val) => handleMCPChange("evaulation", val as McParameterDetailResponseDto)}
                                    cacheLoaded={cacheLoaded}
                                    cachedData={getMcpParameterDetails(896)}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        </Grid>

                        {/* General Appearance */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.general-appearance")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMcParameterDetailSelect
                                    mcpId={897}
                                    value={selectedMCPs.ped_general_view}
                                    handleChange={(val) => handleMCPChange("ped_general_view", val as McParameterDetailResponseDto)}
                                    cacheLoaded={cacheLoaded}
                                    cachedData={getMcpParameterDetails(897)}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        </Grid>

                        {/* Cardiovascular */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.cardiovascular")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMcParameterDetailSelect
                                    mcpId={898}
                                    value={selectedMCPs.ped_cardiovascular}
                                    handleChange={(val) => handleMCPChange("ped_cardiovascular", val as McParameterDetailResponseDto)}
                                    cacheLoaded={cacheLoaded}
                                    cachedData={getMcpParameterDetails(898)}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        </Grid>

                        {/* Respiratory */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.respiratory")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMcParameterDetailSelect
                                    mcpId={899}
                                    value={selectedMCPs.ped_respiratory}
                                    handleChange={(val) => handleMCPChange("ped_respiratory", val as McParameterDetailResponseDto)}
                                    cacheLoaded={cacheLoaded}
                                    cachedData={getMcpParameterDetails(899)}
                                    sx={{ width: "100%" }}
                                />
                            </Grid>
                        </Grid>

                        {/* Total Score */}
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid xs={12} sm={6} md={6} sx={{ borderBottom: "1px solid #E0E0E0" }}>
                                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                                    {t("pediatric-assessment.total-score")}
                                </ByzMuiTypography>
                            </Grid>
                            <Grid xs={12} sm={6} md={6}>
                                <ByzMuiTextField
                                    sx={{ width: "100%" }}
                                    color="info"
                                    variant="outlined"
                                    value={calculatedScore}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <ByzMuiSnackBar
                open={alert.open}
                message={alert.message}
                type={alert.type}
                title={alert.type === "success" ? t("common.success") : t("common.error")}
                onClose={() => setAlert({ ...alert, open: false })}
            />
        </>
    );
}
